import { useTranslation } from '@kgc/i18n';
import type { BreakdownDay } from '../types';
import { summarizeBreakdownDays } from './breakdown-summary';

/**
 * v3-parity step-2 — napi/heti bontás panel (a „Hamarosan"-stub kiváltása).
 *
 * A `pricing.breakdownDays` MÁR-kész adatát jeleníti meg típusonként (munkanap /
 * vasárnap / ünnepnap) + a heti-kedvezmény-napokat + a számlázható napokat. NEM
 * számol árat (az a `recalcPricing` dolga) — csak a meglévő bontást olvasja.
 */
export function DayBreakdownSummary({
  breakdownDays,
  weeklyDiscountDays,
  billableDays,
}: {
  breakdownDays: BreakdownDay[];
  weeklyDiscountDays: number;
  billableDays: number;
}) {
  const { t } = useTranslation();
  if (breakdownDays.length === 0) return null;

  const summary = summarizeBreakdownDays(breakdownDays);

  return (
    <section
      className="mt-4 space-y-1 border-t border-border pt-3"
      data-testid="rental-day-breakdown"
    >
      <h4 className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
        {t('rental.wizard.v2.breakdownHeader')}
      </h4>
      <div
        className="flex items-center justify-between text-xs"
        data-testid="rental-day-breakdown-workdays"
      >
        <span className="text-muted-foreground">{t('rental.wizard.v2.breakdownWorkdays')}</span>
        <span className="tabular-nums">{summary.workdays}</span>
      </div>
      {summary.sundays > 0 && (
        <div
          className="flex items-center justify-between text-xs"
          data-testid="rental-day-breakdown-sundays"
        >
          <span className="text-muted-foreground">{t('rental.wizard.v2.breakdownSundays')}</span>
          <span className="tabular-nums">{summary.sundays}</span>
        </div>
      )}
      {summary.holidays > 0 && (
        <div
          className="flex items-center justify-between text-xs"
          data-testid="rental-day-breakdown-holidays"
        >
          <span className="text-muted-foreground">{t('rental.wizard.v2.breakdownHolidays')}</span>
          <span className="tabular-nums">{summary.holidays}</span>
        </div>
      )}
      <div className="flex items-center justify-between text-xs font-medium border-t border-border pt-1.5 mt-1.5">
        <span>{t('rental.wizard.v2.breakdownBillable')}</span>
        <span className="tabular-nums">{billableDays}</span>
      </div>
      {weeklyDiscountDays > 0 && (
        <div
          className="flex items-center justify-between text-xs text-emerald-600 dark:text-emerald-400"
          data-testid="rental-day-breakdown-weekly"
        >
          <span>{t('rental.wizard.v2.breakdownWeeklyDiscount')}</span>
          <span className="tabular-nums">{weeklyDiscountDays}</span>
        </div>
      )}
    </section>
  );
}
